/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.config = null;
  }

  connectedCallback() {
    const activeStyle = (
      this.getAttribute("active-style") || "bar"
    ).toLowerCase();
    const bulletActiveClass =
      activeStyle === "dot" ? "active-dot" : "active-bar";

    this.config = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 3, spaceBetween: 20 };

    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
      this.config.autoplay
    ) {
      delete this.config.autoplay;
    }

    const pagerColors = this.dataset.pagerColors
      ? this.dataset.pagerColors.split(",").filter((c) => c.trim())
      : [];

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, {
      ...this.config,
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
        paginationBulletMessage: "Go to slide {{index}}",
      },
      navigation: {
        nextEl: this.querySelector(".carousel-next"),
        prevEl: this.querySelector(".carousel-prev"),
      },
      pagination: {
        clickable: true,
        el: this.querySelector(".carousel-pager"),
        bulletClass: "carousel-pager-item",
        bulletActiveClass,
        renderBullet: (index, className) =>
          `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}"></button>`,
      },
      on: {
        init: () => {
          this.classList.remove("opacity-0");
          if (pagerColors.length > 0) {
            this.updatePagerColors(0, pagerColors);
          }
        },
        slideChange: () => {
          if (pagerColors.length > 0) {
            this.updatePagerColors(this.swiper.activeIndex, pagerColors);
          }
        },
      },
    });
  }

  updatePagerColors(activeIndex, pagerColors) {
    const color = pagerColors[activeIndex] || "#651c32";
    const bullets = this.querySelectorAll(".carousel-pager-item");
    bullets.forEach((bullet) => {
      bullet.style.backgroundColor = color;
    });
  }

  previous() {
    if (this.swiper) this.swiper.slidePrev();
  }

  next() {
    if (this.swiper) this.swiper.slideNext();
  }

  slideTo(i) {
    if (this.swiper) this.swiper.slideTo(i);
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
Site Header
*/

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.body = document.querySelector("body");
    this.header = this.querySelector("header");
    this.hamburgerButton = document.querySelector(".hamburger-button-js");
    this.closeButton = document.querySelector(".close-hamburger-button-js");
    this.hamburgerMenuContainer = document.querySelector(
      ".hamburger-menu-content-js",
    );

    this.toggleHamburgerMenu();
  }

  toggleHamburgerMenu() {
    if (this.hamburgerButton) {
      this.hamburgerButton.addEventListener("click", (e) => {
        if (this.hamburgerMenuContainer.classList.contains("opacity-95")) {
          e.target.innerHTML = "Menu";
          this.closeHamburgerMenu();
        } else {
          e.target.innerHTML = "Close";
          this.openHamburgerMenu();
        }
      });
    }
  }

  openHamburgerMenu() {
    this.hamburgerMenuContainer.classList.remove("invisible", "opacity-0");
    this.hamburgerMenuContainer.classList.add("opacity-95");
    this.body.classList.add("overflow-hidden");
  }

  closeHamburgerMenu() {
    this.hamburgerMenuContainer.classList.add("invisible", "opacity-0");
    this.hamburgerMenuContainer.classList.remove("opacity-95");
    this.body.classList.remove("overflow-hidden");
  }

  cartOrFlyoutOpen(ctx) {
    const menuToggle = this.header.querySelector('[data-trigger="toggle-menu"');

    if (this.isMobile) {
      if (ctx === "cart") {
        this.header
          .querySelector('[data-trigger="close-cart"')
          .classList.remove("hidden");

        menuToggle.classList.add("hidden");
      }
      if (ctx === "flyout") {
        menuToggle.querySelector("span:nth-child(1)").classList.add("hidden");
        menuToggle
          .querySelector("span:nth-child(2)")
          .classList.remove("hidden");
      }
      if (ctx === "filters") {
        this.header
          .querySelector('[data-trigger="close-filters"]')
          .classList.remove("hidden");

        menuToggle.classList.add("hidden");
      }
    }
  }
}

customElements.define("site-header", SiteHeader);

/* Newsletter Form */

class NewsletterForm extends HTMLElement {
  connectedCallback() {
    const listId = this.dataset.listId;
    const statusText = this.querySelector(".success");
    this.addEventListener("submit", (e) => {
      e.preventDefault();
      const $form = this.querySelector("form");
      const $email = this.querySelector('[type="email"]');
      const $submit = this.querySelector('[type="submit"]');
      const $error = this.querySelector(".error");
      const submitText = $submit.innerHTML;
      const email = $email.value;
      const body = { email, listId };

      $submit.innerHTML = this.dataset.loadingText;
      $error.classList.add("hidden");

      const config = {
        body: JSON.stringify(body),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
      };

      if (listId) {
        try {
          fetch(
            "https://hunni-serverless.vercel.app/api/ghia/subscribe",
            config,
          )
            .then((res) => res.json())
            .then((data) => {
              $form.reset();
              $email.blur();
              $submit.innerHTML = submitText;
              success.classList.toggle("active");
              window.cvg({
                method: "track",
                eventName: "Subscribed To Newsletter",
                properties: {
                  form: "Footer",
                  formId: "XLSVK5",
                },
                profileProperties: {
                  $email: email,
                },
                aliases: [`urn:email:${email}`],
              });
            });

          if (statusText) {
            statusText.classList.toggle("hidden");
          }
        } catch (e) {
          $submit.innerHTML = submitText;
          $error.classList.remove("hidden");
        }
      }
    });
  }
}

customElements.define("newsletter-form", NewsletterForm);

/*
Select Input
*/

class SelectInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const select = this.querySelector("select");
    const input = select.closest(".text-input");
    if (select.value && input) {
      input.classList.add("active");
    }
    select.addEventListener("change", (e) => {
      this.setActiveValue(e.target.value);
      if (input) {
        input.classList.add("active");
      }
    });
  }

  setActiveValue(val) {
    const options = Array.from(this.querySelectorAll("option"));
    const activeOption = options.find((o) => o.value === val);
    const target = this.querySelector(".select-target");

    if (activeOption) {
      target.innerHTML = activeOption.innerHTML;
    }
  }
}

customElements.define("select-input", SelectInput);

/*
Clover Product Item
*/

class CloverProductItem extends HTMLElement {
  constructor() {
    super();
    this.selectedSellingPlanId = null;
    this.selectedSellingPlanPrice = null;
  }

  connectedCallback() {
    const variantsDataScript = this.querySelector("#selling-plans-data");
    this.variantsData = JSON.parse(variantsDataScript.textContent);

    this.variantButtons = this.querySelectorAll(".variant-option-button");
    this.variantSelect = this.querySelector("#variant-select");
    this.sellingPlanSelect = this.querySelector("#selling-plan-select");
    this.purchaseOption = this.querySelectorAll(
      'input[name^="purchase-option"]',
    );
    this.sellingPlanInput = this.querySelector("#sellin-plan-id");
    this.variantInput = this.querySelector("#variant-id");
    this.atcFullPrice = this.querySelector("#atc-full-price");
    this.atcDiscountPrice = this.querySelector("#atc-discount-price");
    this.subBoxOneTimePrice = this.querySelector("#sub-box-one-time-price");
    this.subBoxSubPrice = this.querySelector("#sub-box-sub-price");

    this.pillInputs = this.querySelectorAll(
      'input[type="radio"][name^="options["]',
    );

    this.oneTimePurchase = this.querySelector("[id^='one-time-purchase']");
    const currentVariantId = new URL(window.location.href).searchParams.get(
      "variant",
    );

    this.selectedVariant =
      this.variantsData.find((variant) => variant.id === currentVariantId) ||
      this.variantsData.find((variant) => variant.available === "true") ||
      this.variantsData[0];

    const finalDiscountSpan = this.querySelector(".final-discount");
    if (finalDiscountSpan) {
      this.defaultSubscriptionDiscountText =
        finalDiscountSpan.textContent.trim();
    }

    this.updateAtcBisButtons();
    this.initEventListeners();
    this.resizeSelect();
  }

  updateSubscriptionDiscountOverride() {
    const span = this.querySelector(".final-discount");
    if (!span || !this.selectedVariant) return;

    const metafields = this.selectedVariant.metafields || {};
    const custom = metafields.custom || {};
    const override = custom.subscription_discount_display_override;

    if (override) {
      span.textContent = `${override}%`;
    } else if (this.defaultSubscriptionDiscountText) {
      span.textContent = this.defaultSubscriptionDiscountText;
    }
  }

  resizeSelect() {
    if (this.variantSelect) {
      const selectedOption = Array.from(
        this.variantSelect.querySelectorAll("option"),
      ).find((option) => option.selected);

      const virtualSelect = this.variantSelect.cloneNode(true);
      virtualSelect.style.position = "absolute";
      virtualSelect.style.visibility = "hidden";
      virtualSelect.style.maxWidth = "none";
      virtualSelect.innerHTML = `<option>${selectedOption.textContent}</option>`;
      this.variantSelect.parentElement.appendChild(virtualSelect);
      const width = virtualSelect.offsetWidth;
      this.variantSelect.style.maxWidth = width < 52 ? "none" : `${width}px`;
      virtualSelect.remove();
    }
  }

  initEventListeners() {
    this.variantButtons.forEach((button) => {
      button.addEventListener("click", (event) =>
        this.handleVariantClick(event, button.dataset.variantId),
      );
    });

    if (this.variantSelect) {
      this.variantSelect.addEventListener("change", (e) => {
        const variantId = e.target.value;

        const productEl = this.closest("clover-product-item");
        if (productEl) {
          const variantInput = productEl.querySelector("#variant-id");
          if (variantInput) {
            variantInput.value = variantId;
          }

          productEl.handleVariantClick(e, variantId);
        }
      });
    }

    this.pillInputs.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        const variantId = e.target.value;
        this.handleVariantClick(e, variantId);
      });
    });

    if (this.purchaseOption) {
      if (this.sellingPlanSelect) {
        this.sellingPlanSelect.addEventListener("change", (e) => {
          const option = this.sellingPlanSelect.querySelector(
            `option[value="${e.target.value}"]`,
          );
          const planId = option.dataset.sellingPlanId;
          if (this.oneTimePurchase && this.oneTimePurchase.checked) {
          } else {
            this.sellingPlanInput.value = planId;
          }
        });
      }

      this.purchaseOption.forEach((radio) => {
        radio.addEventListener("change", () => {
          if (this.oneTimePurchase && this.oneTimePurchase.checked) {
            this.querySelectorAll(".variant-description").forEach((el) => {
              const span = el.querySelector("span");
              if (span) {
                span.innerText = el.dataset.pricePerUnit;
              }
            });

            if (this.sellingPlanInput) {
              this.sellingPlanInput.value = "";
            }
            if (this.atcDiscountPrice) {
              this.atcDiscountPrice.textContent = "";
            }
            if (this.atcFullPrice) {
              this.atcFullPrice.textContent =
                this.selectedVariant.variant_price;
            }
          } else {
            this.querySelectorAll(".variant-description").forEach((el) => {
              const span = el.querySelector("span");
              if (span) {
                span.innerText = el.dataset.subPerUnit;
              }
            });

            this.selectFirstSellingPlan(this.selectedVariant.selling_plans);
          }
        });
      });
    }
  }

  handlePillChange() {
    const selectedOptions = [];

    const optionGroups = {};
    this.pillInputs.forEach((input) => {
      const name = input.name;
      if (!optionGroups[name]) optionGroups[name] = [];
      if (input.checked) optionGroups[name].push(input.value);
    });

    Object.keys(optionGroups).forEach((name) => {
      selectedOptions.push(optionGroups[name][0]);
    });

    if (this.variantSelect) {
      const selectedVariantTitle = Array.from(this.variantSelect.options).find(
        (option) => {
          return selectedOptions.every((opt) => option.text.includes(opt));
        },
      );

      if (selectedVariantTitle) {
        const variantId = selectedVariantTitle.value;
        this.handleVariantClick(null, variantId);
      }
    }
  }

  updateAtcBisButtons() {
    if (!this.selectedVariant) return;

    const atc = this.querySelector(".product-atc");
    const bis = this.querySelector(".klaviyo-bis-trigger");

    const isAvailable =
      this.selectedVariant.available === true ||
      this.selectedVariant.available === "true";

    if (isAvailable) {
      if (atc) atc.classList.remove("hidden");
      if (bis) {
        bis.classList.add("!hidden");
        bis.classList.remove("!block");
      }
    } else {
      if (atc) atc.classList.add("hidden");
      if (bis) {
        bis.classList.remove("!hidden");
        bis.classList.add("!block");
      }
    }
  }

  handleVariantClick(event, variantId) {
    this.selectedVariant = this.variantsData.find(
      (variant) => variant.id === variantId,
    );

    if (this.selectedVariant) {
      const atc = this.querySelector(".product-atc");
      const bis = this.querySelector(".klaviyo-bis-trigger");

      if (this.selectedVariant.available == "true") {
        if (atc) {
          atc.classList.remove("hidden");
        }
        if (bis) {
          bis.classList.add("!hidden");
          bis.classList.remove("!block");
        }
      } else {
        if (atc) {
          atc.classList.add("hidden");
        }
        if (bis) {
          bis.classList.remove("!hidden");
          bis.classList.add("!block");
        }
      }

      this.subBoxOneTimePrice &&
        (this.subBoxOneTimePrice.textContent =
          this.selectedVariant.variant_price);
      if (this.atcFullPrice) {
        this.atcFullPrice.textContent = this.selectedVariant.variant_price;
      }

      if (this.atcDiscountPrice) {
        if (Boolean(this.selectedVariant.variant_compare_at_price)) {
          this.atcDiscountPrice.textContent =
            this.selectedVariant.variant_compare_at_price;
        } else {
          this.atcDiscountPrice.textContent = "";
        }
      }

      const subBoxSub = this.querySelector(".sub-box-sub");
      const firstPlan = this.selectedVariant.selling_plans[0];
      if (firstPlan && firstPlan.price) {
        const priceString = firstPlan.price;
        const currencySymbol = priceString.replace(/[\d.-]/g, "").trim();
        const priceValue = parseFloat(priceString.replace(/[^\d.-]/g, ""));
        const formattedPrice = this.formatPrice(priceValue);

        subBoxSub && subBoxSub.classList.remove("hidden");
        this.subBoxSubPrice &&
          (this.subBoxSubPrice.textContent = formattedPrice);
      } else {
        this.oneTimePurchase && this.oneTimePurchase.click();
        subBoxSub && subBoxSub.classList.add("hidden");
      }

      if (this.variantInput) {
        this.variantInput.value = this.selectedVariant.id;
      }

      if (this.oneTimePurchase && this.oneTimePurchase.checked) {
        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = "";
        }
      } else {
        this.selectFirstSellingPlan(this.selectedVariant.selling_plans);
      }

      this.variantButtons.forEach((btn) =>
        btn.classList.remove("!border-[#AD2725]"),
      );
      const selectedButton = Array.from(this.variantButtons).find(
        (btn) => btn.dataset.variantId === variantId,
      );
      selectedButton && selectedButton.classList.add("!border-[#AD2725]");

      if (this.variantSelect) {
        this.variantSelect.value = variantId;
      }

      this.resizeSelect();

      if (window.location.pathname.includes("/product/")) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variantId);
        window.history.replaceState({}, "", url);
      }
    }

    this.updateSubscriptionDiscountOverride();
  }

  updateSellingPlans(sellingPlans) {
    const matchingSellingPlan = sellingPlans.filter(
      (plan) => plan.selected_plan,
    );
    const subscriptionBox =
      this.sellingPlanSelect?.closest(".subscription-toggle-js") || null;

    if (!this.sellingPlanSelect || !subscriptionBox) return;

    subscriptionBox.classList.toggle("hidden", matchingSellingPlan.length <= 0);
    this.sellingPlanSelect.innerHTML = sellingPlans
      .map((plan) =>
        plan.selected_plan
          ? `
      <option
        id="${plan.plan_id}"
        value="${plan.value}"
        data-selling-plan-id="${plan.plan_id}"
        data-selling-plan-price="${plan.price}"
      >
        ${plan.value.replace("-", " ")}
      </option>
    `
          : "",
      )
      .join("");
  }

  formatPrice(price) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(price)
      .replace(".00", "");
  }

  selectFirstSellingPlan(sellingPlans = []) {
    const selectEl =
      this.container?.querySelector?.("#selling-plan-select") ||
      document.getElementById("selling-plan-select");

    const firstOption = selectEl?.querySelector("option") || null;
    const firstPlan = sellingPlans.find((p) => p.selected_plan);

    let chosenPlan = firstPlan || null;

    if (selectEl && selectEl.options.length > 0) {
      selectEl.selectedIndex = 0;
    }

    if (firstOption && (!firstPlan || firstOption.id !== firstPlan.plan_id)) {
      const match = sellingPlans.find((p) => p.plan_id === firstOption.id);
      if (match) chosenPlan = match;
    }

    if (!chosenPlan && sellingPlans.length) {
      chosenPlan = sellingPlans[0];
    }

    if (!chosenPlan) return;

    this.selectedSellingPlanId = chosenPlan.plan_id;
    this.selectedSellingPlanPrice = chosenPlan.price;

    if (typeof this.selectedSellingPlanPrice === "string") {
      const priceValue = parseFloat(
        this.selectedSellingPlanPrice.replace(/[^\d.-]/g, ""),
      );
      const formattedPrice = this.formatPrice(priceValue);

      if (this.atcFullPrice) this.atcFullPrice.textContent = formattedPrice;
      if (this.atcDiscountPrice)
        this.atcDiscountPrice.textContent = this.selectedVariant.variant_price;
    }

    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = this.selectedSellingPlanId;
    }
  }
}

customElements.define("clover-product-item", CloverProductItem);

/*
  Simple Collapse
*/

class SimpleCollapse extends HTMLElement {
  connectedCallback() {
    this.initEventListeners();
  }

  initEventListeners() {
    const collapseHeader = this.querySelector(".collapse-header");
    const collapseContent = this.querySelector(".collapse-content");

    if (collapseContent && !collapseContent.id) {
      SimpleCollapse._uid = (SimpleCollapse._uid || 0) + 1;
      collapseContent.id = `simple-collapse-content-${SimpleCollapse._uid}`;
    }

    if (collapseHeader) {
      if (collapseHeader.tagName !== "BUTTON") {
        collapseHeader.setAttribute("role", "button");
        collapseHeader.setAttribute("tabindex", "0");
        collapseHeader.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.toggleCollpase();
          }
        });
      }
      if (collapseContent) {
        collapseHeader.setAttribute("aria-controls", collapseContent.id);
      }
      collapseHeader.setAttribute(
        "aria-expanded",
        collapseContent && collapseContent.classList.contains("expanded")
          ? "true"
          : "false",
      );
    }

    collapseHeader.addEventListener("click", () => this.toggleCollpase());
  }
  toggleCollpase() {
    const collapseHeader = this.querySelector(".collapse-header");
    const collapseContent = this.querySelector(".collapse-content");
    const collapseIcon = this.querySelector(".collapse-icon");

    if (collapseContent.classList.contains("expanded")) {
      collapseContent.style.maxHeight = "0px";
      collapseContent.classList.remove("expanded");
      if (collapseHeader) {
        collapseHeader.setAttribute("aria-expanded", "false");
      }
      if (!collapseIcon.dataset.isEdit) {
        collapseIcon.style.transform = "rotate(0deg)";
      }
    } else {
      collapseContent.style.maxHeight = collapseContent.scrollHeight + "px";
      collapseContent.classList.add("expanded");
      if (collapseHeader) {
        collapseHeader.setAttribute("aria-expanded", "true");
      }
      if (!collapseIcon.dataset.isEdit) {
        collapseIcon.style.transform = "rotate(45deg)";
      }
    }
  }
}

customElements.define("simple-collapse", SimpleCollapse);

/*
  Smooth Scroll
*/

class SmoothScroll extends HTMLElement {
  constructor() {
    super();
    if (!this.hasAttribute("role")) this.setAttribute("role", "link");
    if (!this.hasAttribute("tabindex")) this.setAttribute("tabindex", "0");
    this._initSmoothScroll();
  }

  _scrollToTarget() {
    const targetId = this.getAttribute("href").substring(1);
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      const offset = 100;
      const elementPosition =
        targetElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }

  _initSmoothScroll() {
    this.addEventListener("click", (e) => {
      e.preventDefault();
      this._scrollToTarget();
    });

    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this._scrollToTarget();
      }
    });
  }
}

customElements.define("smooth-scroll", SmoothScroll);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Cart toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define("cart-toggle", CartToggle);

let lastInputWasKeyboard = false;
document.addEventListener("keydown", () => { lastInputWasKeyboard = true; }, true);
["pointerdown", "mousedown", "touchstart"].forEach((type) => {
  document.addEventListener(type, () => { lastInputWasKeyboard = false; }, true);
});

/*
Cart Item
*/

class CartItem extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.cart = this.closest("site-cart");
    this.itemTotals = this.querySelector(".item-totals");
    this.quantityInputEl = this.querySelector("number-input input");
    this.cartIndex = parseInt(this.dataset.cartIndex, 10);
    this.variantId = this.dataset.variantId;

    this.addRemoveBundleEvent();
    this.addSubscriptionHandlers();
    this.addQuantityChangeEvent();
    this.addRemoveEvent();
  }

  showLoader() {
    if (this.itemTotals) {
      this.itemTotals.firstElementChild.classList.add("hidden");
      this.itemTotals.lastElementChild.classList.remove("hidden");
    }
  }

  hideLoader() {
    if (this.itemTotals) {
      this.itemTotals.firstElementChild.classList.remove("hidden");
      this.itemTotals.lastElementChild.classList.add("hidden");
    }
  }

  addSubscriptionHandlers() {
    const upgradeButtonEl = this.querySelector(".upgrade-to-sub-js");
    const sellingPlanSelectEl = this.querySelector(".selling-plan-select");

    if (upgradeButtonEl && sellingPlanSelectEl) {
      upgradeButtonEl.addEventListener("click", () => {
        sellingPlanSelectEl.focus();
        sellingPlanSelectEl.click();
      });

      sellingPlanSelectEl.addEventListener("change", async () => {
        const planId = sellingPlanSelectEl.value;
        if (!planId) return;

        const quantity = this.quantityInputEl
          ? parseInt(this.quantityInputEl.value, 10)
          : 1;
        await this.convertLineToSubscription({ planId, quantity });
      });
    }
  }

  async convertLineToSubscription({ planId, quantity }) {
    this.showLoader();

    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ line: this.cartIndex, quantity: 0 }),
      });
      await this.cart.addVariantToCart(
        this.variantId,
        quantity,
        parseInt(planId, 10),
      );

      this.hideLoader();
      this.cart.handleRewards();
    } catch (err) {
      alert(err.message);
    }
  }

  addQuantityChangeEvent() {
    if (this.quantityInputEl) {
      this.quantityInputEl.addEventListener("change", () => {
        this.showLoader();

        this.cart.updateQuantity(this.cartIndex, +this.quantityInputEl.value);
      });
    }
  }

  addRemoveEvent() {
    const itemRemove = this.querySelector(".item-remove");

    if (itemRemove) {
      itemRemove.addEventListener("click", async (e) => {
        this.showLoader();

        const updates = { [this.variantId]: 0 };
        await this.cart.updateCart(updates);
        this.cart.handleRewards();
      });
    }
  }

  addRemoveBundleEvent() {
    const bundleRemove = document.querySelector(".bundle-remove");

    if (bundleRemove) {
      bundleRemove.addEventListener("click", async (e) => {
        const bundleItems = Array.from(
          this.cart.querySelectorAll("[data-bundle-id]"),
        );
        bundleItems.forEach((item) => {
          item.showLoader();
        });

        const updates = bundleItems.reduce((acc, item) => {
          return { ...acc, [item.variantId]: 0 };
        }, {});

        await this.cart.updateCart(updates);
        await this.cart.handleRewards();
      });
    }
  }
}

customElements.define("cart-item", CartItem);

/*
  Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
    this._lastFocused = null;
    this._onKeydown = null;
  }

  connectedCallback() {
    this.cart = document.querySelector(".site-cart");
    this.handleRewards();
  }

  getFocusableElements() {
    return Array.from(
      this.cart.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]',
      ),
    ).filter(
      (el) =>
        el.getAttribute("tabindex") !== "-1" && el.getClientRects().length > 0,
    );
  }

  setToggleExpanded(expanded) {
    document
      .querySelectorAll('[aria-controls="CartDrawer"]')
      .forEach((toggle) => {
        toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      });
  }

  openCart() {
    if (this.cart) {
      this.cart.classList.add("active");
      document.body.style.overflow = "hidden";
      this.setToggleExpanded(true);
      this._lastFocused = document.activeElement;

      window.setTimeout(() => {
        const closeButton = this.cart.querySelector("#close-menu");
        const focusables = this.getFocusableElements();
        const target = lastInputWasKeyboard ? closeButton || focusables[0] : this.cart;
        if (target) target.focus({ preventScroll: true });
      }, 100);

      this._onKeydown = (e) => {
        if (e.key === "Escape") {
          this.closeCart();
          return;
        }
        if (e.key !== "Tab") return;

        const focusables = this.getFocusableElements();
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
          if (active === first || !this.cart.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else if (active === last || !this.cart.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      };
      document.addEventListener("keydown", this._onKeydown);
    }
  }

  closeCart() {
    if (this.cart) {
      this.cart.classList.remove("active");
      document.body.removeAttribute("style");
      this.setToggleExpanded(false);

      if (this._onKeydown) {
        document.removeEventListener("keydown", this._onKeydown);
        this._onKeydown = null;
      }

      if (this._lastFocused && document.contains(this._lastFocused)) {
        this._lastFocused.focus();
      }
      this._lastFocused = null;
    }
  }

  toggleCart() {
    if (this.cart.classList.contains("active")) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  getCartSections() {
    return [
      {
        section: "main-cart-items",
        selector: "#main-cart-items",
      },
      {
        section: "main-cart-footer",
        selector: "#main-cart-footer",
      },
      {
        section: "main-cart-header",
        selector: "#main-cart-header",
      },
      {
        section: "main-cart-recommendations",
        selector: "#main-cart-recommendations",
      },
      {
        section: "header",
        selector: "#header",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    const el = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return el ? el.innerHTML : "";
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      const elToReplace = document.querySelector(activeSection.selector);
      if (elToReplace) {
        const html = this.getSectionInnerHTML(
          sections[sectionKey],
          activeSection.selector,
        );
        if (elToReplace) {
          elToReplace.innerHTML = html;
        }
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  addVariantToCart(id, quantity = 1, sellingPlan) {
    const items = [
      sellingPlan
        ? { id, quantity, selling_plan: sellingPlan }
        : { id, quantity },
    ];
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    if (!this.cart.classList.contains("active")) {
      this.toggleCart();
    }

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.ghiaReport &&
            window.ghiaReport("cart_error", {
              error_message: response.description || response.message,
              error_stage: "add_rejected",
              variant_id: items[0] && items[0].id,
              selling_plan: sellingPlan || "none",
              http_status: response.status,
            });
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);
        this.handleRewards();
        document.dispatchEvent(new CustomEvent("cart:updated"));
      })
      .catch((e) => {
        window.ghiaReport &&
          window.ghiaReport("cart_error", {
            error_message: e && e.message ? e.message : String(e),
            error_stage: "add_exception",
            variant_id: items[0] && items[0].id,
            selling_plan: sellingPlan || "none",
          });
      });
  }

  updateQuantity(line, quantity) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then((response) => {
        this.renderCartSections(response.sections);
        this.handleRewards();
        document.dispatchEvent(new CustomEvent("cart:updated"));
      })
      .catch((e) => {
        window.ghiaReport &&
          window.ghiaReport("cart_error", {
            error_message: e && e.message ? e.message : String(e),
            error_stage: "change_exception",
          });
      });
  }

  updateCart(updates, shouldRefresh = true) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    console.log(updates);

    return fetch("/cart/update", config)
      .then((response) => response.json())
      .then((response) => {
        if (shouldRefresh) {
          this.renderCartSections(response.sections);
        }
        document.dispatchEvent(new CustomEvent("cart:updated"));
      })
      .catch(() => {
        // handle error
      });
  }

  addSellingPlan(variantId, sellingPlan) {
    const sections = this.getCartSections().map((s) => s.section);
    const data = {
      id: variantId,
      selling_plan: sellingPlan,
      sections,
    };
    const body = JSON.stringify(data);
    const config = this.buildConfig(body);

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then((response) => {
        this.renderCartSections(response.sections);
        this.addSelectEvents();
        document.dispatchEvent(new CustomEvent("cart:updated"));
      })
      .catch(() => {
        // handle error
      });
  }

  async addBundleVariantToCart(parentVariantId, childVariantsId, sellingPlan) {
    const getStayAiGuid = () => {
      let d = new Date().getTime();
      let d2 =
        (performance && performance.now && performance.now() * 1000) || 0;
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        let r = Math.random() * 16;
        if (d > 0) {
          r = ((d + r) % 16) | 0;
          d = Math.floor(d / 16);
        } else {
          r = ((d2 + r) % 16) | 0;
          d2 = Math.floor(d2 / 16);
        }
        return (c == "x" ? r : (r & 0x7) | 0x8).toString(16);
      });
    };
    const currentCartResponse = await fetch("/cart.js");
    const currentCartJson = await currentCartResponse.json();
    const token = currentCartJson.token.split("?key")[0];
    const stayAiGuid = getStayAiGuid();
    const customBundleEl = document.querySelector("custom-bundle");
    const bundleData = customBundleEl.bundleData.data;

    const cartItems = childVariantsId.map((childVariantId) => {
      return {
        id: childVariantId,
        quantity: 1,
        properties: {
          __pdqId: token,
          _bundle_id: stayAiGuid,
        },
        selling_plan: sellingPlan,
      };
    });

    const items = [
      {
        id: parentVariantId,
        quantity: 1,
        properties: {
          __pdqId: token,
          _bundle_id: stayAiGuid,
          _bundle_parent: true,
          _bundle_max: 12,
          _bundle_min: 3,
        },
        selling_plan: sellingPlan,
      },
      ...cartItems,
    ];

    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    if (!this.cart.classList.contains("active")) {
      this.toggleCart();
    }

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.ghiaReport &&
            window.ghiaReport("cart_error", {
              error_message: response.description || response.message,
              error_stage: "add_rejected",
              variant_id: items[0] && items[0].id,
              selling_plan: sellingPlan || "none",
              http_status: response.status,
            });
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);
        this.handleRewards();
        document.dispatchEvent(new CustomEvent("cart:updated"));
      })
      .catch((e) => {
        window.ghiaReport &&
          window.ghiaReport("cart_error", {
            error_message: e && e.message ? e.message : String(e),
            error_stage: "add_exception",
            variant_id: items[0] && items[0].id,
            selling_plan: sellingPlan || "none",
          });
      });
  }

  setCartNote(note) {
    const body = JSON.stringify({ note });
    const config = this.buildConfig(body);
    return fetch("/cart/update", config)
      .then((response) => response.json())
      .then((response) => {
        this.renderCartSections(response.sections);
        document.dispatchEvent(new CustomEvent("cart:updated"));
      })
      .catch(() => {
        // handle error
      });
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + "cart.js";
    const resp = await fetch(uri, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return await resp.json();
  }

  async handleRewards() {
    const cart = await this.fetchCart();
    const rewards = this.querySelector("cart-rewards");
    if (!rewards) return;

    const subtotal = cart.total_price;
    const shippingThreshold = Number(rewards.dataset.shippingThreshold);
    const giftThreshold = Number(rewards.dataset.giftThreshold);
    const giftEnabled = rewards.dataset.giftEnabled === "true";

    const hasSubscription =
      Array.isArray(cart.items) &&
      cart.items.some(
        (item) =>
          !!item.selling_plan_allocation ||
          !!item.selling_plan ||
          !!item.selling_plan_id ||
          (item.properties &&
            ("shipping_interval_unit_type" in item.properties ||
              "subscription_id" in item.properties ||
              "subscription" in item.properties)),
      );

    const formatCurrency = (amount) =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      })
        .format(amount / 100)
        .replace(".00", "");

    const toggleIconState = (type, isAchieved) => {
      const iconWrapper = rewards.querySelector(`[data-icon="${type}"]`);
      if (!iconWrapper) return;

      iconWrapper.classList.toggle("bg-burgundy", isAchieved);

      const img = iconWrapper.querySelector(".icon-image");
      const check = iconWrapper.querySelector(".icon-check");

      if (img) img.classList.toggle("hidden", isAchieved);
      if (check) check.classList.toggle("hidden", !isAchieved);
    };

    const emptyMsg = rewards.querySelector('[data-status="empty"]');
    const pendingMsg = rewards.querySelector('[data-status="pending"]');
    const mixedMsg = rewards.querySelector(
      '[data-status="shipping-earned-gift-pending"]',
    );
    const earnedMsg = rewards.querySelector('[data-status="earned"]');

    [emptyMsg, pendingMsg, mixedMsg, earnedMsg].forEach((el) =>
      el?.classList.add("hidden"),
    );

    const shippingMet = hasSubscription || subtotal >= shippingThreshold;
    const giftMet = giftEnabled && subtotal >= giftThreshold;

    toggleIconState("shipping", shippingMet);
    toggleIconState("gift", giftEnabled && giftMet);

    if (subtotal === 0 && !hasSubscription) {
      emptyMsg?.classList.remove("hidden");
    } else if (shippingMet && giftEnabled && giftMet) {
      earnedMsg?.classList.remove("hidden");
    } else if (shippingMet && giftEnabled && !giftMet) {
      const giftRemaining = giftThreshold - subtotal;
      const giftSpan = mixedMsg?.querySelector(".gift-remaining");
      if (giftSpan) giftSpan.textContent = formatCurrency(giftRemaining);
      mixedMsg?.classList.remove("hidden");
    } else if (!shippingMet) {
      const shippingRemaining = shippingThreshold - subtotal;
      const shippingSpan = pendingMsg?.querySelector(".shipping-remaining");
      if (shippingSpan)
        shippingSpan.textContent = formatCurrency(shippingRemaining);

      if (giftEnabled) {
        const giftRemaining = giftThreshold - subtotal;
        const giftSpan = pendingMsg?.querySelector(".gift-remaining");
        if (giftSpan) giftSpan.textContent = formatCurrency(giftRemaining);
      }
      pendingMsg?.classList.remove("hidden");
    } else if (shippingMet && !giftEnabled) {
      earnedMsg?.classList.remove("hidden");
    }

    let nextThreshold;
    if (giftEnabled) {
      const thresholds = [shippingThreshold, giftThreshold].sort(
        (a, b) => a - b,
      );
      nextThreshold = thresholds.find((t) => subtotal < t) || thresholds[1];
    } else {
      nextThreshold = shippingThreshold;
    }

    // Bar: 100% if subscription unlocks shipping
    const progress = hasSubscription
      ? 100
      : Math.min((subtotal / nextThreshold) * 100, 100);

    const bar = rewards.querySelector(".percentage-bar");
    if (bar) bar.style.width = `${progress}%`;
  }
}

customElements.define("site-cart", SiteCart);

/*
Number Input
*/

class NumberInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.target.closest("button");

    if (button) {
      button.name === "increment" ? this.input.stepUp() : this.input.stepDown();

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  }
}

customElements.define("number-input", NumberInput);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      const form = e.target.closest("form");
      e.preventDefault();
      const isBundleForm = this.dataset.isBundleForm === "true";
      const $id = form.querySelector('[name="id"]');
      const $qty = form.querySelector('[name="quantity"]');
      const $sellingPlan = form.querySelector('[name="selling_plan"]');
      const qty = $qty ? $qty.value : 1;
      const sellingPlan =
        $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value.trim() : null;

      if ($id) {
        const cart = document.querySelector("site-cart");
        if (cart) {
          if (isBundleForm) {
            const variantId = document.querySelector(".tab-input-button.active")
              .dataset.variantId;
            const customBundleEl = document.querySelector("custom-bundle");
            const selectedItems = customBundleEl?.selectedItems;

            await cart.addBundleVariantToCart(
              variantId,
              selectedItems.map(({ id }) => id),
              sellingPlan,
            );
          } else {
            await cart.addVariantToCart($id.value, qty, sellingPlan);
          }
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const cart = document.querySelector("site-cart");
      const idx = this.dataset.index;

      if (cart) {
        await cart.updateQuantity(idx, 0);
      }
    });
  }
}

customElements.define("cart-remove", CartRemove);

/*
Dynamic Select
*/

class DynamicSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector("select");
    this.label = this.querySelector("label");
    if (this.select) {
      this.updateLabel(this.select);
      this.initEventListeners();
    }
  }

  initEventListeners() {
    this.select.addEventListener("change", () => {
      this.updateLabel(this.select);
      this.resizeSelect(this.select);
    });
  }

  updateLabel(select) {
    if (!this.label || !select) return;
    const selectedOption = select.selectedOptions[0];
    this.label.textContent = selectedOption ? selectedOption.textContent : "";
  }

  resizeSelect(select) {
    if (!select) return;

    const container = this.querySelector(".select-container");
    const selectedOption = select.selectedOptions[0];

    const virtualSelect = document.createElement("span");
    virtualSelect.style.position = "absolute";
    virtualSelect.style.visibility = "hidden";
    virtualSelect.style.whiteSpace = "nowrap";
    virtualSelect.textContent = selectedOption.textContent;
    document.body.appendChild(virtualSelect);

    const width = virtualSelect.offsetWidth;
    container.style.minWidth = `${width + 32}px`;
    select.style.minWidth = `${width + 32}px`;

    virtualSelect.remove();
  }
}

customElements.define("dynamic-select", DynamicSelect);

/*
Header Menu
*/

class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.header = this.closest(".site-header");
    this.bar = this.querySelector("#header");
    const navMin = parseInt(this.getAttribute("data-nav-min"), 10) || 1280;
    this.desktop = window.matchMedia(`(min-width: ${navMin}px)`);
    this.canHover = window.matchMedia("(hover: hover)");
    this.active = null;
    this.pairs = [];

    this.bind();

    if (this.bar) {
      this.observer = new MutationObserver(() => {
        if (this.pairs.length && this.pairs.every((p) => this.contains(p.trigger))) return;
        this.reset();
        this.bind();
      });
      this.observer.observe(this.bar, { childList: true });
    }

    this.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !this.active) return;
      const pair = this.active;
      const returnFocus = pair.panel.contains(document.activeElement);
      this.close();
      if (returnFocus) pair.trigger.focus();
    });

    this.onOutside = (e) => {
      const pair = this.active;
      if (!pair) return;
      if (!pair.panel.contains(e.target) && !pair.trigger.contains(e.target)) this.close();
    };
    document.addEventListener("pointerdown", this.onOutside);

    this.onViewport = () => {
      if (!this.desktop.matches) this.close();
    };
    this.desktop.addEventListener("change", this.onViewport);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    if (this.observer) this.observer.disconnect();
    if (this.onOutside) document.removeEventListener("pointerdown", this.onOutside);
    if (this.onViewport) this.desktop.removeEventListener("change", this.onViewport);
  }

  get isOpen() {
    return Boolean(this.active);
  }

  bind() {
    this.pairs = Array.from(this.querySelectorAll("[data-menu-trigger]"))
      .map((trigger) => {
        const id = trigger.getAttribute("aria-controls");
        const panel = id ? this.querySelector("#" + id) : null;
        return panel ? { trigger, panel } : null;
      })
      .filter(Boolean);

    this.pairs.forEach((pair) => {
      pair.trigger.addEventListener("mouseenter", () => this.schedule(pair, 60));
      pair.trigger.addEventListener("mouseleave", () => this.schedule(null, 160));
      pair.panel.addEventListener("mouseenter", () => this.schedule(pair, 0));
      pair.panel.addEventListener("mouseleave", () => this.schedule(null, 160));

      pair.trigger.addEventListener("click", (e) => {
        if (this.canHover.matches || this.active === pair) return;
        e.preventDefault();
        this.open(pair);
      });

      pair.trigger.addEventListener("keydown", (e) => {
        if (e.key !== "ArrowDown" && e.key !== " ") return;
        e.preventDefault();
        this.open(pair);
        const first = pair.panel.querySelector("a[href]");
        if (first) first.focus();
      });

      pair.panel.addEventListener("focusout", (e) => {
        if (!pair.panel.contains(e.relatedTarget) && e.relatedTarget !== pair.trigger) this.close();
      });
    });
  }

  reset() {
    clearTimeout(this.timer);
    if (this.active && this.header) this.header.classList.remove("active", "shop-menu-open");
    this.active = null;
  }

  schedule(pair, delay) {
    if (!this.canHover.matches) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => (pair ? this.open(pair) : this.close()), delay);
  }

  open(pair) {
    clearTimeout(this.timer);
    if (!this.desktop.matches || !pair) return;
    if (this.active === pair) return;
    if (this.active) this.hide(this.active);
    this.active = pair;
    pair.panel.classList.add("is-open");
    pair.trigger.setAttribute("aria-expanded", "true");
    if (this.header) this.header.classList.add("active", "shop-menu-open");
  }

  hide(pair) {
    pair.panel.classList.remove("is-open");
    pair.trigger.setAttribute("aria-expanded", "false");
  }

  close() {
    clearTimeout(this.timer);
    if (!this.active) return;
    this.hide(this.active);
    this.active = null;
    if (this.header) this.header.classList.remove("active", "shop-menu-open");
  }
}

customElements.define("header-menu", HeaderMenu);

/*
Pill Selector
*/

class PillSelector extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener("change", (e) => {
        const name = input.name;
        const allLabels = this.querySelectorAll(`input[name="${name}"]`);

        allLabels.forEach((radio) => {
          radio
            .closest("label")
            .classList.remove("!bg-burgundy", "!text-cream");
          radio.closest("label").classList.add("text-burgundy");
        });

        if (input.checked) {
          input.closest("label").classList.add("!bg-burgundy", "!text-cream");
          input.closest("label").classList.remove("text-burgundy");

          const variantId = input.value;
          const descriptions = this.querySelectorAll(".variant-description");
          descriptions.forEach((el) => {
            el.classList.toggle("hidden", el.dataset.variantId !== variantId);
          });
        }
      });
    });
  }
}

customElements.define("pill-selector", PillSelector);

/*
  Variant Selector Swipe
*/

class VariantSelectorSwipe extends HTMLElement {
  connectedCallback() {
    this.setupListeners();
  }

  setupListeners() {
    const select = this.querySelector("#variant-select");
    const carousels = document.querySelectorAll(".media-carousel");

    if (select) {
      // const rawAssociatedGalleryImageIndex =
      //     select.querySelector("option:checked")?.dataset.associatedGalleryImageIndex;
      // const associatedGalleryImageIndex = rawAssociatedGalleryImageIndex ? +rawAssociatedGalleryImageIndex - 1 : null;
      // this.syncCarouselToVariant(select.value, carousels, associatedGalleryImageIndex);

      select.addEventListener("change", (e) => {
        const variantId = e.target.value;

        const rawAssociatedGalleryImageIndex =
          e.target.querySelector("option:checked").dataset
            .associatedGalleryImageIndex;
        const associatedGalleryImageIndex = rawAssociatedGalleryImageIndex
          ? +rawAssociatedGalleryImageIndex - 1
          : null;

        const productEl = this.closest("clover-product-item");
        if (productEl) {
          const variantInput = productEl.querySelector("#variant-id");
          if (variantInput) {
            variantInput.value = variantId;
          }

          productEl.handleVariantClick(e, variantId);
        }

        this.syncCarouselToVariant(
          variantId,
          carousels,
          associatedGalleryImageIndex,
        );
      });
    }

    const radioInputs = this.querySelectorAll('input[type="radio"]');
    radioInputs.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        const variantId = e.target.value;
        const associatedGalleryImageIndex = e.target.dataset
          .associatedGalleryImageIndex
          ? +e.target.dataset.associatedGalleryImageIndex - 1
          : null;

        const productEl = this.closest("clover-product-item");
        if (productEl) {
          const variantInput = productEl.querySelector("#variant-id");
          if (variantInput) {
            variantInput.value = variantId;
          }

          productEl.handleVariantClick(e, variantId);
        }

        this.syncCarouselToVariant(
          variantId,
          carousels,
          associatedGalleryImageIndex,
        );
      });
    });
  }

  syncCarouselToVariant(variantId, carousels, associatedGalleryImageIndex) {
    if (associatedGalleryImageIndex !== 0 && !associatedGalleryImageIndex)
      return;
    carousels.forEach((carousel) => {
      console.log(
        "### associatedGalleryImageIndex",
        associatedGalleryImageIndex,
      );
      if (!carousel) return;
      const slides = Array.from(carousel.querySelectorAll(".swiper-slide"));
      const selectedSlide = slides.find(
        (slide) => slide.dataset.variantId === variantId,
      );
      if (selectedSlide) {
        carousel.activeIndex = slides.indexOf(selectedSlide);
        carousel.slideTo(associatedGalleryImageIndex);
      }
    });
  }
}

customElements.define("variant-selector-swipe", VariantSelectorSwipe);

/*
  Checkout Button
*/

class CheckoutButton extends HTMLElement {
  connectedCallback() {
    const button = this.querySelector("button");

    button.addEventListener("click", async (e) => {
      button.innerText = "Checkout Out...";

      const fromVal = document.querySelector('input[name="note_from"]').value;
      const toVal = document.querySelector('input[name="note_to"]').value;
      const message = document.querySelector(
        'textarea[name="note_message"]',
      ).value;
      const messageVal = message.replace(/\n/g, " ");

      if (messageVal) {
        const giftNote = `
          ${fromVal ? `From: ${fromVal}` : ""}
          ${toVal ? `To: ${toVal}` : ""}
          ${messageVal ? `Message: ${messageVal}` : ""}
        `;
        const cart = document.querySelector("site-cart");
        await cart.setCartNote(giftNote);
      }

      window.location.href = "/checkout";
    });
  }
}

customElements.define("checkout-button", CheckoutButton);

class GiftNoteSubmit extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.variantId = this.dataset.variantId;
    this.noteToInput = this.querySelector('input[name="note_to"]');
    this.noteFromInput = this.querySelector('input[name="note_from"]');
    this.noteMessageInput = this.querySelector('textarea[name="note_message"]');
    this.submitBtn = this.querySelector(".gift-note-submit-btn");
    this.collapseIcon =
      this.closest("simple-collapse").querySelector(".collapse-icon");

    this.initEventListeners();
    this.loadNoteFromCart();
  }

  initEventListeners() {
    this.submitBtn.addEventListener("click", () => this.handleSubmit());

    document.addEventListener("cart:updated", () => this.checkVariantInCart());
  }

  async handleSubmit() {
    const toVal = this.noteToInput.value;
    const fromVal = this.noteFromInput.value;
    const message = this.noteMessageInput.value;
    const messageVal = message.replace(/\n/g, " ");

    if (!messageVal.trim()) {
      return;
    }

    this.setSubmittingState(true);

    const giftNote = `From: ${fromVal}
To: ${toVal}
Message: ${messageVal}`;

    const cart = document.querySelector("site-cart");

    if (cart) {
      await cart.setCartNote(giftNote);

      if (this.variantId && this.variantId.trim()) {
        const cartData = await fetch("/cart.js").then((r) => r.json());
        const giftItemIndex = cartData.items.findIndex(
          (item) => item.properties && item.properties._gift_note_item,
        );

        if (giftItemIndex === -1) {
          await this.addVariantToCart();
        } else {
          await this.updateGiftItemProperties(
            giftItemIndex + 1,
            toVal,
            fromVal,
            messageVal,
          );
        }
      }

      this.updateCollapseIcon(true);
      this.closeCollapse();
      this.setSubmittingState(false);
    }
  }

  setSubmittingState(isSubmitting) {
    if (isSubmitting) {
      this.submitBtn.disabled = true;
      this.submitBtn.dataset.originalText = this.submitBtn.textContent;
      this.submitBtn.textContent = "Submitting...";
    } else {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent =
        this.submitBtn.dataset.originalText || "Submit";
    }
  }

  async addVariantToCart() {
    const sections = [
      "main-cart-items",
      "main-cart-footer",
      "main-cart-header",
      "main-cart-recommendations",
      "header",
    ];

    try {
      const toVal = this.noteToInput.value;
      const fromVal = this.noteFromInput.value;
      const message = this.noteMessageInput.value;
      const messageVal = message.replace(/\n/g, " ");

      const addResponse = await fetch(`/cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(this.variantId),
              quantity: 1,
              properties: {
                _gift_note_item: "true",
                _sort_order: "999",
                To: toVal || "",
                From: fromVal || "",
                Message: messageVal || "",
              },
            },
          ],
          sections,
        }),
      });

      const addData = await addResponse.json();

      if (addData.status || addData.errors) {
        console.warn("Could not add gift variant to cart:", addData);
        return;
      }

      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.renderCartSections(addData.sections);
        cart.handleRewards();
        document.dispatchEvent(new CustomEvent("cart:updated"));
      }
    } catch (error) {
      console.warn("Error adding gift variant to cart:", error);
    }
  }

  async updateGiftItemProperties(lineIndex, toVal, fromVal, message) {
    // replace \n by spaces to avoid issues with JSON
    const messageVal = message.replace(/\n/g, " ");
    const sections = [
      "main-cart-items",
      "main-cart-footer",
      "main-cart-header",
      "main-cart-recommendations",
      "header",
    ];

    try {
      const updateResponse = await fetch(`/cart/change.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          line: lineIndex,
          properties: {
            _gift_note_item: "true",
            _sort_order: "999",
            To: toVal || "",
            From: fromVal || "",
            Message: messageVal || "",
          },
          sections,
        }),
      });

      const updateData = await updateResponse.json();

      if (updateData.status || updateData.errors) {
        console.warn("Could not update gift item properties:", updateData);
        return;
      }

      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.renderCartSections(updateData.sections);
        cart.handleRewards();
        document.dispatchEvent(new CustomEvent("cart:updated"));
      }
    } catch (error) {
      console.warn("Error updating gift item properties:", error);
    }
  }

  closeCollapse() {
    const collapse = this.closest("simple-collapse");
    if (collapse) {
      collapse.toggleCollpase();
    }
  }

  async loadNoteFromCart() {
    try {
      const resp = await fetch("/cart.js");
      const data = await resp.json();

      if (data && data.note) {
        this.parseAndPopulateNote(data.note);
        this.updateCollapseIcon(true);
      }
    } catch (error) {
      console.error("Error loading cart note:", error);
    }
  }

  parseAndPopulateNote(note) {
    const fromMatch = note.match(/From:\s*(.+?)(?:\n|$)/);
    const toMatch = note.match(/To:\s*(.+?)(?:\n|$)/);
    const messageMatch = note.match(/Message:\s*(.+?)(?:\n|$)/s);

    if (fromMatch && fromMatch[1]) {
      this.noteFromInput.value = fromMatch[1].trim();
    }
    if (toMatch && toMatch[1]) {
      this.noteToInput.value = toMatch[1].trim();
    }
    if (messageMatch && messageMatch[1]) {
      this.noteMessageInput.value = messageMatch[1].trim();
    }
  }

  updateCollapseIcon(hasNote) {
    if (hasNote) {
      this.collapseIcon.innerHTML =
        '<span class="text-sm underline">Edit</span>';
      this.collapseIcon.dataset.isEdit = "true";
      this.collapseIcon.style.transform = "none";
    } else {
      this.collapseIcon.innerHTML = `<svg width="16" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.51465 1.01489L9.51465 17.9855" stroke="#661B32" stroke-width="1.5" stroke-linecap="round"/>
        <path d="M18 9.50012L1.02944 9.50012" stroke="#661B32" stroke-width="1.5" stroke-linecap="round"/>
      </svg>`;
      delete this.collapseIcon.dataset.isEdit;
    }
  }

  async checkVariantInCart() {
    if (!this.variantId || !this.variantId.trim()) {
      return;
    }

    try {
      const resp = await fetch("/cart.js");
      const data = await resp.json();

      const hasVariant = data.items.some(
        (item) => item.variant_id.toString() === this.variantId,
      );

      if (!hasVariant && data.note) {
        await this.clearNote();
      }
    } catch (error) {
      console.error("Error checking variant in cart:", error);
    }
  }

  async clearNote() {
    const cart = document.querySelector("site-cart");
    if (cart) {
      await cart.setCartNote("");
      this.noteToInput.value = "";
      this.noteFromInput.value = "";
      this.noteMessageInput.value = "";
      this.updateCollapseIcon(false);
    }
  }
}

customElements.define("gift-note-submit", GiftNoteSubmit);

/*
  Press Grid Section
*/

class PressGrid extends HTMLElement {
  connectedCallback() {
    this.ctaButton = this.querySelector(".button");
    this.maskContainer = this.querySelector(".mask-container");

    if (this.ctaButton) {
      this.ctaButton.addEventListener("click", () =>
        this.expandGridContainer(),
      );
    }

    this.observeVideos();
  }

  observeVideos() {
    const videos = this.querySelectorAll("video");
    if (!videos.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 },
    );

    videos.forEach((video) => observer.observe(video));
  }

  playAllVideos() {
    this.querySelectorAll("video").forEach((video) => {
      video.play().catch(() => {});
    });
  }

  expandGridContainer() {
    this.mainContent = this.querySelector(".main-content-js");

    if (this.mainContent.classList.contains("max-h-[750px]")) {
      this.mainContent.classList.remove("max-h-[750px]");
      this.maskContainer.classList.add("hidden");
      this.ctaButton.innerText = "View Less";
      this.playAllVideos();
    } else {
      this.mainContent.classList.add("max-h-[750px]");
      this.maskContainer.classList.remove("hidden");
      this.ctaButton.innerText = "View More";
    }
  }
}

customElements.define("press-grid", PressGrid);

/*
Klaviyo Signup
*/

class KlaviyoSignup extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (this.form) {
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.submitForm(e);
      });
    }
  }

  async submitForm(e) {
    const error = this.querySelector(".error");
    const success = this.querySelector(".success");
    const email = this.form.querySelector('[type="email"]');
    const submit = this.form.querySelector('[type="submit"]');
    const listId = this.dataset.listId;
    const submitText = submit.innerHTML;

    error.innerHTML = "";
    submit.innerHTML = "Submitting...";

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email.value,
                subscriptions: {
                  email: { marketing: { consent: "SUBSCRIBED" } },
                },
              },
            },
          },
        },
        ...(listId && {
          relationships: { list: { data: { type: "list", id: listId } } },
        }),
      },
    };

    try {
      const resp = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=LL98XZ`,
        {
          method: "POST",
          headers: {
            Accept: "application/vnd.api+json",
            "Content-Type": "application/vnd.api+json",
            revision: "2025-01-15",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(
          errorData.errors?.[0]?.detail ||
            "Klaviyo subscription request failed",
        );
      }

      this.form.reset();
      submit.innerHTML = submitText;
      success.classList.remove("hidden");
      email.blur();
    } catch (e) {
      error.classList.remove("hidden");
      error.innerHTML =
        e.message || "Oops, there was an error. Please try again.";
      submit.innerHTML = submitText;
    }
  }
}

customElements.define("klaviyo-signup", KlaviyoSignup);

/* Main Product Checker */

class ProductChecker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.productHandle = this.dataset.productHandle;
    this.currentUrl = new URL(window.location.href);
    this.productId = this.dataset.productId;
    this.checkProductHandle();

    if (this.productHandle === "giftcard") {
      this.observeVariantChanges();
    }
  }

  checkProductHandle() {
    if (this.productHandle == "giftcard") {
      this.updateUrl(this.productId);
    }
  }

  observeVariantChanges() {
    const variantInput = document.querySelector("#variant-id");
    if (variantInput) {
      const observer = new MutationObserver(() => {
        if (variantInput.value) {
          this.updateUrl(variantInput.value);
        }
      });

      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ["value"],
      });

      variantInput.addEventListener("input", () => {
        if (variantInput.value) {
          this.updateUrl(variantInput.value);
        }
      });

      variantInput.addEventListener("change", () => {
        if (variantInput.value) {
          this.updateUrl(variantInput.value);
        }
      });
    }
  }

  updateUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url.toString());
  }
}
customElements.define("product-checker", ProductChecker);

/*
Smart Marquee  
*/

class SmartMarquee extends HTMLElement {
  constructor() {
    super();
    this._raf = null;
    this._supportsHover =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover)").matches;
    this._wrapper = null;
    this._onResize = this._onResize.bind(this);
    this._onPauseEnter = this._onPauseEnter.bind(this);
    this._onPauseLeave = this._onPauseLeave.bind(this);
    this._pauseTargets = new Set();
    this._sectionId = null;
  }

  connectedCallback() {
    this._sectionId =
      this.getAttribute("data-section-id") ||
      this.closest("[data-section-id]")?.getAttribute("data-section-id") ||
      null;

    const direction = (this.getAttribute("direction") || "").toLowerCase();
    if (direction === "reverse") {
      this.setAttribute("data-direction", "reverse");
    } else {
      this.removeAttribute("data-direction");
    }

    this.refresh();
    this._attachPauseTargets();

    // Re-measure when track size changes (e.g. images loading after preview nav)
    const baseTrack = this.querySelector('[data-smart-marquee-track="base"]');
    if (baseTrack && typeof ResizeObserver !== "undefined") {
      this._trackObserver = new ResizeObserver(() => {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.refresh());
      });
      this._trackObserver.observe(baseTrack);
    }

    window.addEventListener("resize", this._onResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._onResize);
    this._detachPauseTargets();
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._trackObserver) this._trackObserver.disconnect();
  }

  _onResize() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this.refresh());
  }

  _attachPauseTargets() {
    const sectionRoot = this._sectionId
      ? document.querySelector(`[data-section-id="${this._sectionId}"]`)
      : null;

    const scope = sectionRoot || this;
    const targets = Array.from(
      scope.querySelectorAll("[data-smart-marquee-pause]"),
    ).filter((el) => el.closest("smart-marquee") === this);

    targets.forEach((el) => {
      if (this._pauseTargets.has(el)) return;
      this._pauseTargets.add(el);
      el.addEventListener("mouseenter", this._onPauseEnter);
      el.addEventListener("mouseleave", this._onPauseLeave);
    });
  }

  _detachPauseTargets() {
    this._pauseTargets.forEach((el) => {
      el.removeEventListener("mouseenter", this._onPauseEnter);
      el.removeEventListener("mouseleave", this._onPauseLeave);
    });
    this._pauseTargets.clear();
  }

  _onPauseEnter() {
    this.pause();
  }

  _onPauseLeave() {
    this.resume();
  }

  _setPausedState(isPaused) {
    this._wrapper =
      this._wrapper || this.querySelector(".smart-marquee-wrapper");
    if (!this._wrapper) return;

    try {
      const animations = this._wrapper.getAnimations
        ? this._wrapper.getAnimations()
        : [];
      animations.forEach((a) => (isPaused ? a.pause() : a.play()));
    } catch (_) {}

    this._wrapper.style.animationPlayState = isPaused ? "paused" : "running";
  }

  pause() {
    this.classList.add("is-paused");
    this._setPausedState(true);
  }

  resume() {
    this.classList.remove("is-paused");
    this._setPausedState(false);
  }

  refresh() {
    const wrapper = this.querySelector(".smart-marquee-wrapper");
    if (!wrapper) {
      return;
    }
    this._wrapper = wrapper;

    const containerWidth = this.getBoundingClientRect().width;
    if (!containerWidth) return;

    // Keep looping enabled always so the marquee never "stops" on wide screens.
    this.classList.add("is-looping");

    // Set animation duration based on track width so speed (px/s) is constant.
    const baseTrack = wrapper.querySelector('[data-smart-marquee-track="base"]');
    if (baseTrack) {
      const pxPerSecond = parseFloat(this.getAttribute("data-speed")) || 50;
      const trackWidth = baseTrack.getBoundingClientRect().width;
      const duration = Math.round((trackWidth / pxPerSecond) * 100) / 100;
      const next = `${duration}s`;
      if (duration > 0 && wrapper.style.animationDuration !== next) {
        const animation = wrapper.getAnimations ? wrapper.getAnimations()[0] : null;
        const timing = animation && animation.effect ? animation.effect.getComputedTiming() : null;
        const progress = timing && typeof timing.progress === "number" ? timing.progress : null;
        wrapper.style.animationDuration = next;
        if (animation && progress !== null) {
          try {
            animation.currentTime = progress * duration * 1000;
          } catch (_) {}
        }
      }
    }

    this._attachPauseTargets();

    if (this.classList.contains("is-paused")) {
      this._setPausedState(true);
    }
  }
}

customElements.define("smart-marquee", SmartMarquee);

if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
  const pauseAutoplayVideos = () => {
    document.querySelectorAll("video[autoplay]").forEach((video) => {
      video.removeAttribute("autoplay");
      video.pause();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", pauseAutoplayVideos);
  } else {
    pauseAutoplayVideos();
  }
}
